/**
 * MenuItem component.
 */
import Focus from '@shared/types/enums/focus';
import ItemContainer from '@components/atoms/container/menuItemContainer';
import IconContainer from '@components/atoms/container/menuItemIconContainer';
import ListContext from '@shared/components/utils/context/listContext';
import MenuContext from '@shared/components/utils/context/menuContext';
import TextContainer from '@components/atoms/container/menuItemTextContainer';
import TranslationManager from '@shared/assets/locale/translationsManager';
import type { MenuItem as MenuItemType } from '@shared/components/menu/menuData';
import { DURATION, TRANSITION } from '@shared/types/constants/animations';
import { focusSelector } from '@shared/store/slices/focus.slice';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useMemo, useState } from 'react';

/**
 * Props for the menu item component.
 */
interface Props {
  focused?: boolean;
  index?: number;
  item: MenuItemType;
  onClick: (index?: number) => void;
}

/**
 * Displays a menu item with an icon and text.
 * @param parentProps - The props for the menu item component.
 * @returns {JSX.Element} - The menu item component.
 */
export default function MenuItem(parentProps: Props) {
  const { props, text } = useMenuItem(parentProps);
  const { item } = parentProps;
  const { Icon } = item;
  return (
    <ItemContainer {...props.itemContainer}>
      <IconContainer {...props.iconContainer}>
        {Icon ? <Icon {...props.iconElement} /> : null}
      </IconContainer>
      <TextContainer {...props.textContainer}>{text}</TextContainer>
    </ItemContainer>
  );
}

/**
 * Hook that handles the focused state of the menu item and the enter and focus actions.
 * @param parentProps - The props for the menu item component.
 * @returns {Object} - The props for the menu item components: container, icon, item.
 */
function useMenuItem(parentProps: Props) {
  // Selectors
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);

  // Context
  const { listIndex } = useContext(ListContext);
  const { collapse, display, effectiveIndex } = useContext(MenuContext);

  // States
  const [hovered, setHovered] = useState(false);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onPointerEnter = useCallback(() => {
    if (!display) return;
    setHovered(true);
  }, [display]);

  const onPointerLeave = useCallback(() => {
    if (!display) return;
    setHovered(false);
  }, [display]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const { index, item, onClick } = parentProps;
    const itemSelected =
      index === listIndex && effectiveIndex.current === index;
    const itemFocused = itemSelected && focus === Focus.MENU;
    return {
      props: {
        iconContainer: {
          collapse,
          duration: DURATION.MENU_ITEM_OPACITY,
          focused: itemSelected || hovered,
          transition: TRANSITION.MENU_ITEM_ICON(collapse),
        },
        iconElement: {
          focused: itemSelected || hovered,
          fromMenu: true,
        },
        itemContainer: {
          collapse,
          focused: itemFocused && itemSelected,
          onClick: () => onClick(index),
          onPointerEnter,
          onPointerLeave,
        },
        textContainer: {
          collapse,
          duration: DURATION.MENU_ITEM_OPACITY,
          focused: itemFocused || hovered,
          transition: TRANSITION.MENU_ITEM_TEXT,
        },
      },
      text: TranslationManager.get(item.text),
    };
  }, [
    collapse,
    enabled,
    focus,
    hovered,
    effectiveIndex,
    listIndex,
    parentProps,
  ]);
}
